import _get from "lodash/get";
import _round from "lodash/round";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { WEIGHT_UNITS } from "../../../../../constants";
import { getExerciseName } from "../../../../../parts/modals/learn-more/blockHelpers";
import SvgIcon from "../../../../../parts/SvgIcon";
import { RillContext } from "../../../../../rill/RillReact/share/base";
import { formattedTarget, getSetCounts } from "../../../../../utils/actuals";
import { formatMetconResult } from "../../../../../utils/metcon";
import { isPROutcome } from "../../../../../utils/personal-records";

function LogTrainingCard({ exercises, prExerciseKeys, repMaxPR, dayKey }) {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useContext(RillContext);
  const { activeUser, activeUserToRequest, fromMetric, withWeight } = locals;

  if (!exercises || exercises.length === 0) return null;

  const units = activeUser.units || "metric";
  const precision = units === "metric" ? 2 : 1;
  const intensityMeasure =
    _get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";

  const allCompleted = exercises.every((exercise) => {
    if (exercise.primary === "metcon") {
      return exercise.metconResult?.status === "completed";
    }
    return (exercise.sets || []).every((elem) => {
      const { total, completed, missed } = getSetCounts(exercise, elem);
      return total > 0 && completed + missed === total;
    });
  });

  const sessionHasPr = prExerciseKeys?.size > 0;

  return (
    <div className="log-card log-card--training">
      <div className="log-card__header">
        <div className="log-card__header-start">
          <SvgIcon src="/img/new-icons/barbell.svg" useCurrentColor={true} />
          <span>{t("calendar.cards.training.title")}</span>
        </div>
        <div className="log-card__header-icons">
          {allCompleted && (
            <div className="bg-(--training-day) rounded-full w-4 h-4 flex items-center justify-center">
              <SvgIcon
                src="/img/new-icons/check.svg"
                classNames="log-card__header-icon-end text-(--background-color)"
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                }}
                useCurrentColor={true}
              />
            </div>
          )}
        </div>
      </div>
      <div className="log-card__body">
        {exercises.map((exercise) => {
          const actualLoads = [];
          (exercise.sets || []).forEach((elem) => {
            if (elem.outcomes && Object.keys(elem.outcomes).length > 0) {
              Object.values(elem.outcomes).forEach((outcome) => {
                const rawLoad = outcome?.outcome?.load;
                if (rawLoad) {
                  const converted = fromMetric(rawLoad, units);
                  if (converted) {
                    actualLoads.push(_round(converted, precision));
                  }
                }
              });
            }
          });

          const shownPRReps = new Set();

          let firstVideo = null;
          let firstVideoSetIndex = 0;
          let firstVideoIndex = 0;
          for (let si = 0; si < (exercise.sets || []).length; si++) {
            const set = exercise.sets[si];
            const entries = Object.entries(set.videos || {});
            const found = entries.find(([, v]) => v?.videoOriginal);
            if (found) {
              firstVideoSetIndex = si;
              firstVideoIndex = found[0];
              firstVideo = found[1];
              break;
            }
          }

          return (
            <div key={exercise._id} className="log-card__exercise">
              <div className="log-card__exercise-name">
                {getExerciseName(exercise)}
              </div>
              {(exercise.sets || []).map((elem, idx) => {
                const load = elem.load
                  ? _round(fromMetric(elem.load, units), precision)
                  : 0;
                const targetLoad = !!load && withWeight(load, units);
                const target = formattedTarget(
                  elem,
                  exercise.type === "primary" || exercise.type === "secondary",
                  elem.load ? `${_round(elem.percentRepMax, 1)}` : "",
                  "metric",
                  targetLoad,
                  intensityMeasure,
                  isRiR,
                  false
                );
                if (!target) return null;

                let setPrMatched = false;
                if (repMaxPR && elem.outcomes) {
                  const prOutcome = Object.values(elem.outcomes).find((o) => {
                    const r = isPROutcome(exercise, o, repMaxPR, dayKey);
                    return r !== null && !shownPRReps.has(r.reps);
                  });
                  if (prOutcome) {
                    const r = isPROutcome(
                      exercise,
                      prOutcome,
                      repMaxPR,
                      dayKey
                    );
                    shownPRReps.add(r.reps);
                    setPrMatched = true;
                  }
                }

                return (
                  <div
                    key={`${exercise._id}-${idx}`}
                    className="log-card__target"
                  >
                    <span className="log-card__target-text">{target}</span>
                    {setPrMatched && <span className="pr-star">★</span>}
                  </div>
                );
              })}
              {actualLoads.length > 0 && (
                <div className="log-card__actuals">
                  {actualLoads.join(", ")} {WEIGHT_UNITS[units]}
                </div>
              )}
              {exercise.primary === "metcon" &&
                exercise.metconResult &&
                formatMetconResult(exercise.metconResult) && (
                  <div className="log-card__actuals">
                    {formatMetconResult(exercise.metconResult)}
                  </div>
                )}
              {firstVideo && (
                <a
                  className="log-card__video-link"
                  href={`/actions/refresh?modal[type]=exercise-set-video&originalId=${firstVideo.videoOriginal}&uploaderId=${firstVideo.uploaderId}&exerciseToEdit=${exercise._id}&setIndex=${firstVideoSetIndex}&videoIndex=${firstVideoIndex}&reviewedByTheCoach=${firstVideo.reviewedByTheCoach}`}
                >
                  <SvgIcon
                    src="/img/new-icons/play-white.svg"
                    useCurrentColor={true}
                  />
                  {t("calendar.cards.training.viewVideo")}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LogTrainingCard;
